import { convertToRaw, EditorState } from 'draft-js';
import moment from 'moment';
import firebase from '../../firebase-integration/index';
import { ILibraryListConfig } from '../../store/library/reducers';
import { ILibraryPost } from '../interfaces/library';
import { searchTerms } from '../utils';

const db = firebase.firestore();
const libraryCollection = db.collection('library');

export const createLibraryPost =
  async (userId: string, post: ILibraryPost) => {
    const oldId = {
      originalId: post.id,
    };

    delete post.id;

    const date = firebase.firestore.FieldValue.serverTimestamp();
    const newLibraryContent = await libraryCollection
      .add({
        ...post,
        ...oldId,
        addedOn: date,
        searchTerms: searchTerms([post.title]),
        authorSearch: searchTerms([post.author.displayName]),
        owner: userId,
      });

    await libraryCollection
      .doc(newLibraryContent.id)
      .collection('note')
      .doc(newLibraryContent.id)
      .set({
        id: newLibraryContent.id,
        note: JSON.stringify(convertToRaw(EditorState.createEmpty().getCurrentContent())),
      });

    return {
      post: {
        ...post,
        id: newLibraryContent.id,
        date: moment().format('MM/DD/YYYY'),
        creation: moment().format('MM/DD/YYYY HH:mm:ss ZZ'),
        updated: moment().format('MM/DD/YYYY HH:mm:ss ZZ'),
      },
    };
  };

export const deleteLibraryPost =
  async (postId: string) => {
    return await libraryCollection
      .doc(postId)
      .update({
        archived: true,
        updated: firebase.firestore.FieldValue.serverTimestamp(),
      });
  };

export const getLibraryPosts =
  async (userId: string, config: ILibraryListConfig) => {
    const documentQuery = libraryCollection
      .where('archived', '==', false)
      .where('owner', '==', userId);

    // Utility Builders
    // const queryBuilder = (validator: boolean, queryStatement: any[]) => (baseQuery: any) =>
    //   validator
    //     ? baseQuery.where(...queryStatement)
    //     : baseQuery;

    const orderQueryBuilder = (validator: boolean, key: string) => (baseQuery: any) => {
      return validator ? baseQuery.orderBy(key.split('-')[0], 'desc') : baseQuery.orderBy(key.split('-')[0]);
    };

    const paginationBuilder = (lastItem: object | null) => (baseQuery: any) =>
      config.sort.includes('-descend') && !config.pagination.lastItem
        ? baseQuery.endBefore(lastItem)
        : baseQuery.startAfter(lastItem);

    const searchTermQuery = (baseQuery: any) => {
      return config.search
        ? baseQuery.where('searchTerms', 'array-contains', config.search)
        : baseQuery;
    };

    const authorQuery = (baseQuery: any) => {
      return config.authorSearch
        ? baseQuery.where('authorSearch', 'array-contains', config.authorSearch)
        : baseQuery;
    };

    const orderedQuery = (key: string) => orderQueryBuilder(
      key.includes('-descend'),
      key,
    );

    const paginate = paginationBuilder(config.pagination.lastItem);

    const buildQuery = (query: any) => [query]
      .map(searchTermQuery)
      .map(orderedQuery(config.sort))
      .map(paginate)
      .reduce((acc, value) => {
        return value;
      }, {});

    const results = await buildQuery(documentQuery)
      .limit(config.pagination.limit)
      .get();

    const posts: any[] = [];
    results.forEach((data: any) => {
      posts.push({
        ...data.data(),
        date: moment(data.data().date).format('MM/DD/YYYY'),
        addedOn: moment(data.data().addedOn.seconds * 1000).format('MM/DD/YYYY'),
        updated: moment(data.data().updated.seconds * 1000).format('MM/DD/YYYY HH:mm:ss ZZ'),
        creation: moment(data.data().creation.seconds * 1000).format('MM/DD/YYYY HH:mm:ss ZZ'),
        id: data.id,
        highlights: [],
      });
    });

    return {
      posts,
      lastItem: results.docs[results.docs.length - 1],
    };
  };

export const updateLibraryNote =
  async (libraryId: string, note: any) => {
    console.log('I am updating the Library', note);
    return await libraryCollection
      .doc(libraryId)
      .collection('note')
      .doc(libraryId)
      .update(note);
  };

export const getLibraryNote =
  async (libraryId: string) => {
    const note = await libraryCollection
      .doc(libraryId)
      .collection('note')
      .doc(libraryId)
      .get();

    return {
      id: note.id,
      note: note.data()?.note,
    };
  };
